import { create } from "zustand";
import { persist } from "zustand/middleware";
import convex from "../../../convexClient";
import { api } from "../../../convex/_generated/api";
import { useTenantStore } from "../../../stores/useTenantStore";
import useThemeStore from "../../../stores/useThemeStore";
import useAccountStore from "../../FinanceManager/Accounts/Store/useAccountStore";
import { getBrowserTimeZone } from "../../../utils/timeZone";
import { normalizeUserProfile } from "../../../utils/userProfile";

export const useUserStore = create(
  persist(
    (set, get) => ({
      user: null,
      config: { currency: "GHS", openLastBudget: false, timeZone: "UTC" },
      calendarConnections: [],
      error: null,
      successMessage: null,
      isLoading: false,
      initialized: false,
      showPassword: false,

      // ---------------------------------
      // STATE HANDLERS
      // ---------------------------------
      setUser: (user) => set({ user: normalizeUserProfile(user), error: null }),
      clearUser: () => set({ user: null }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      setSuccess: (msg) => set({ successMessage: msg }),
      clearSuccess: () => set({ successMessage: null }),
      clearSuccessMessage: () => set({ successMessage: null }),
      setLoading: (val) => set({ isLoading: val }),
      togglePasswordVisibility: () =>
        set((state) => ({ showPassword: !state.showPassword })),

      mapAuthErrorToMessage: (err) => {
        const msg = err?.message || "";
        if (
          msg.includes("Invalid password") ||
          msg.includes("wrong password") ||
          msg.includes("Could not find account") ||
          msg.includes("Invalid credentials") ||
          msg.includes("InvalidAccountId") ||
          msg.includes("InvalidSecret")
        ) {
          return "Invalid email or password.";
        }
        if (msg.includes("already exists") || msg.includes("already registered")) {
          return "This email is already in use.";
        }
        if (
          msg.includes('Failed to insert or update a document in table "users"') ||
          msg.includes("does not match the schema")
        ) {
          return "Authentication is temporarily unavailable. Please try again shortly.";
        }
        if (msg.includes("Too many")) {
          return "Too many attempts. Please try again later.";
        }
        if (msg.includes("network") || msg.includes("fetch")) {
          return "Network error. Check your connection and try again.";
        }
        return msg || "Something went wrong. Please try again.";
      },

      // Keep old name as alias for backward compat with components
      mapFirebaseErrorToMessage: (err) => get().mapAuthErrorToMessage(err),

      validatePassword: (password) => {
        if (!password || password.length < 8)
          return "Password must be at least 8 characters.";
        if (!/[A-Z]/.test(password))
          return "Password must include an uppercase letter.";
        if (!/[a-z]/.test(password))
          return "Password must include a lowercase letter.";
        if (!/[0-9]/.test(password))
          return "Password must include a number.";
        return "";
      },

      asyncHandler: async (fn) => {
        const { setError, setLoading } = get();
        setLoading(true);
        try {
          const result = await fn();
          setLoading(false);
          return result;
        } catch (err) {
          console.error(err);
          setError(err.message || "Something went wrong.");
          setLoading(false);
          throw err;
        }
      },

      // ---------------------------------
      // PROFILE SYNC (called by SyncAuthToStore)
      // ---------------------------------
      syncProfile: (profile) => {
        const normalizedProfile = normalizeUserProfile(profile);
        if (!normalizedProfile) return;
        set({
          user: {
            uid: normalizedProfile.uid,
            email: normalizedProfile.email,
            displayName: normalizedProfile.displayName,
            photoURL: normalizedProfile.photoURL,
          },
          config: {
            currency: normalizedProfile.currency || "GHS",
            openLastBudget: normalizedProfile.openLastBudget ?? false,
            timeZone: normalizedProfile.timeZone || "UTC",
          },
        });
        if (normalizedProfile.theme) {
          useThemeStore.getState().loadSavedTheme(normalizedProfile.theme);
        }
      },

      clearProfile: () => {
        set({
          user: null,
          config: { currency: "GHS", openLastBudget: false, timeZone: "UTC" },
          calendarConnections: [],
          initialized: true,
        });
        useTenantStore.getState().clearTenant();
        useAccountStore.getState().resetSession();
      },

      // Clear all persisted store data from localStorage on sign-out.
      // The actual Convex Auth session signOut must be called separately
      // via useAuthActions().signOut() in the component (it's a hook).
      clearAllPersistedData: () => {
        const PERSISTED_KEYS = [
          "user-store",
          "tenant-store",
          "gm-ui-store",
          "reporting-store",
          "transaction-store",
          "account-store",
          "goal-store",
          "financial-planning-store",
          "budget-storage",
          "pending-workspace-name",
        ];
        for (const key of PERSISTED_KEYS) {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        }
        get().clearProfile();
      },

      // ---------------------------------
      // PROFILE MANAGEMENT
      // ---------------------------------
      fetchConfiguration: async () => {
        const { asyncHandler } = get();
        return await asyncHandler(async () => {
          const profile = await convex.query(api.users.getProfile, {});
          if (profile) {
            set({
              config: {
                currency: profile.currency || "GHS",
                openLastBudget: profile.openLastBudget ?? false,
                timeZone: profile.timeZone || "UTC",
              },
            });
            if (profile.theme) {
              useThemeStore.getState().loadSavedTheme(profile.theme);
            }
          }
        });
      },

      saveConfiguration: async (currency, openLastBudget) => {
        const { asyncHandler, setSuccess } = get();
        const timeZone = getBrowserTimeZone();
        await asyncHandler(async () => {
          await convex.mutation(api.users.saveConfiguration, {
            currency,
            openLastBudget,
            timeZone,
          });
          set({ config: { currency, openLastBudget, timeZone } });
          setSuccess("Configuration saved successfully!");
        });
      },

      updateProfileInfo: async (displayName, email) => {
        const { asyncHandler, setSuccess } = get();
        await asyncHandler(async () => {
          const normalizedProfile = normalizeUserProfile({ displayName, email });

          await convex.mutation(api.users.updateProfile, {
            displayName: normalizedProfile?.displayName ?? null,
            email: normalizedProfile?.email ?? null,
          });

          set((state) => ({
            user: normalizeUserProfile({
              ...state.user,
              email: normalizedProfile?.email ?? state.user?.email ?? "",
              displayName:
                normalizedProfile?.displayName ?? state.user?.displayName ?? "",
            }),
          }));

          setSuccess("Profile updated successfully!");
        });
      },

      fetchCalendarConnections: async () => {
        const { asyncHandler } = get();
        return await asyncHandler(async () => {
          const connections = await convex.query(
            api.calendarSync.listConnections,
            {}
          );
          set({ calendarConnections: connections });
          return connections;
        });
      },

      beginCalendarConnect: async (provider, returnPath) => {
        const { asyncHandler } = get();
        await asyncHandler(async () => {
          const result = await convex.action(api.calendarSync.beginConnect, {
            provider,
            returnPath,
          });
          window.location.assign(result.authorizationUrl);
        });
      },

      disconnectCalendar: async (provider) => {
        const { asyncHandler, setSuccess } = get();
        await asyncHandler(async () => {
          await convex.action(api.calendarSync.disconnect, { provider });
          await get().fetchCalendarConnections();
          setSuccess("Calendar disconnected successfully.");
        });
      },

      retryCalendarSync: async (provider) => {
        const { asyncHandler, setSuccess } = get();
        await asyncHandler(async () => {
          const scheduled = await convex.action(
            api.calendarSync.retryFailedSync,
            { provider }
          );
          await get().fetchCalendarConnections();
          setSuccess(
            scheduled > 0
              ? `Retry scheduled for ${scheduled} sync job${scheduled === 1 ? "" : "s"}.`
              : "No failed sync jobs to retry."
          );
        });
      },
    }),
    {
      name: "user-store",
      partialize: (state) => ({
        user: state.user,
        config: state.config,
        calendarConnections: state.calendarConnections,
      }),
    }
  )
);
